import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import projectService from '../../../services/projectService';
import clientService from '../../../services/clientService';
import { supabase } from '../../../lib/supabase';

const CreateProjectModal = ({ onClose, onSubmit, editingProject }) => {
  const [formData, setFormData] = useState({
    name: '',
    projectNo: '',
    description: '',
    owner: '',
    imageUrl: null,
    clientId: '',
    projectType: '',
    projectCountry: ''
  });
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [showNewProjectTypeForm, setShowNewProjectTypeForm] = useState(false);
  const [newProjectType, setNewProjectType] = useState('');

  // Predefined project types
  const projectTypes = [
    'Residential',
    'Commercial',
    'Industrial',
    'Infrastructure',
    'Healthcare',
    'Education',
    'Hospitality',
    'Mixed-Use'
  ];

  // Comprehensive list of countries
  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
    'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia',
    'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
    'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia',
    'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
    'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
    'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
    'Haiti', 'Honduras', 'Hungary',
    'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Jamaica', 'Japan', 'Jordan',
    'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
    'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
    'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
    'North Macedonia', 'Norway',
    'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar',
    'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
    'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
    'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain',
    'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago',
    'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
    'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen',
    'Zambia', 'Zimbabwe'
  ];

  // Fetch clients and staff on mount
  useEffect(() => {
    fetchClients();
    fetchStaff();
  }, []);

  const fetchClients = async () => {
    setLoadingClients(true);
    const { data, error } = await clientService?.getAllClients();
    if (!error && data) {
      const transformedClients = data?.map(client => ({
        id: client?.id,
        companyName: client?.company_name,
        status: client?.status
      }))?.filter(c => c?.status === 'active');
      setClients(transformedClients);
    }
    setLoadingClients(false);
  };

  const fetchStaff = async () => {
    setLoadingStaff(true);
    const { data, error } = await supabase?.from('workers')?.select('id, name, role')?.order('name');
    
    if (!error && data) {
      setStaff(data);
    }
    setLoadingStaff(false);
  };

  // Populate form when editing
  useEffect(() => {
    if (editingProject) {
      setFormData({
        name: editingProject?.name || '',
        projectNo: editingProject?.projectNo || '',
        description: editingProject?.description || '',
        owner: editingProject?.owner || '',
        imageUrl: editingProject?.imageUrl || null,
        clientId: editingProject?.clientId || '',
        projectType: editingProject?.projectType || '',
        projectCountry: editingProject?.projectCountry || ''
      });
      setImagePreview(editingProject?.imageUrl || null);
    }
  }, [editingProject]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!validTypes?.includes(file?.type)) {
        setErrors({ ...errors, image: 'Please select a valid image file (JPEG, PNG, or WebP)' });
        return;
      }

      // Validate file size (2MB)
      if (file?.size > 2097152) {
        setErrors({ ...errors, image: 'Image size must be less than 2MB' });
        return;
      }

      setImageFile(file);
      setErrors({ ...errors, image: '' });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData({ ...formData, imageUrl: null });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Project name is required';
    if (!formData?.projectNo?.trim()) newErrors.projectNo = 'Project number is required';
    if (!formData?.description?.trim()) newErrors.description = 'Description is required';
    if (!formData?.owner?.trim()) newErrors.owner = 'Project owner is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setUploading(true);
    let imageUrl = formData?.imageUrl;

    // Upload image if new file selected
    if (imageFile) {
      const projectId = editingProject?.id || 'temp-' + Date.now();
      const { data, error } = await projectService?.uploadProjectImage(imageFile, projectId);
      if (error) {
        setErrors({ ...errors, image: `Image upload failed: ${error}` });
        setUploading(false);
        return;
      }
      imageUrl = data?.url;
    }

    const submitData = {
      ...formData,
      imageUrl
    };

    await onSubmit(submitData);
    setUploading(false);
  };

  const handleCreateNewClient = async () => {
    if (!newClientName?.trim()) return;
    
    const { data, error } = await clientService?.createClient({
      companyName: newClientName,
      status: 'active'
    });
    
    if (!error && data) {
      await fetchClients();
      setFormData({ ...formData, clientId: data?.id });
      setShowNewClientForm(false);
      setNewClientName('');
    }
  };

  const handleCreateNewProjectType = () => {
    if (!newProjectType?.trim()) return;
    setFormData({ ...formData, projectType: newProjectType });
    setShowNewProjectTypeForm(false);
    setNewProjectType('');
  };

  // Transform clients for Select component
  const clientOptions = clients?.map(client => ({
    value: client?.id,
    label: client?.companyName
  })) || [];

  // Transform staff for Select component
  const staffOptions = staff?.map(worker => ({
    value: worker?.name,
    label: `${worker?.name}${worker?.role ? ` (${worker?.role})` : ''}`
  })) || [];

  // Transform project types for Select component
  const projectTypeOptions = projectTypes?.map(type => ({
    value: type,
    label: type
  })) || [];

  // Transform countries for Select component
  const countryOptions = countries?.map(country => ({
    value: country,
    label: country
  })) || [];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={editingProject ? "Edit" : "FolderPlus"} size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {editingProject ? 'Edit Project' : 'Create New Project'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {editingProject ? 'Update project details' : 'Set up a new workflow project'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-5">
            {/* Project Image Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Project Image Logo</label>
              <div className="flex items-start gap-4">
                {imagePreview ? (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-border">
                    <img
                      src={imagePreview}
                      alt="Project logo preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-1 right-1 w-6 h-6 bg-destructive rounded-full flex items-center justify-center hover:bg-destructive/80 transition-colors"
                    >
                      <Icon name="X" size={14} color="white" />
                    </button>
                  </div>
                ) : (
                  <label className="w-32 h-32 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                    <Icon name="Upload" size={24} color="var(--color-muted-foreground)" />
                    <span className="text-xs text-muted-foreground mt-2">Upload Image</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/jpg"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                )}
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">
                    Upload a project logo image (optional)
                  </p>
                  <p className="text-xs text-muted-foreground mb-1">
                    <strong>Recommended:</strong> 200x200px square image
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supported formats: JPEG, PNG, WebP (Max 2MB)
                  </p>
                  {errors?.image && (
                    <p className="text-sm text-destructive mt-2">{errors?.image}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Project No. and Project Name - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Project No."
                placeholder="Enter project number"
                value={formData?.projectNo}
                onChange={(e) => handleInputChange('projectNo', e?.target?.value)}
                error={errors?.projectNo}
                required
              />

              <Input
                label="Project Name"
                placeholder="Enter project name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />
            </div>

            {/* Project Type and Project Country - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              {/* Project Type Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Project Type</label>
                {!showNewProjectTypeForm ? (
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <Select
                        options={projectTypeOptions}
                        value={formData?.projectType}
                        onChange={(value) => handleInputChange('projectType', value)}
                        placeholder="Select project type (optional)"
                        searchable={false}
                        clearable={true}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowNewProjectTypeForm(true)}
                      className="px-3 py-2 rounded-md border border-input bg-background hover:bg-muted transition-colors flex-shrink-0"
                      title="Add custom project type"
                    >
                      <Icon name="Plus" size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter custom project type"
                        value={newProjectType}
                        onChange={(e) => setNewProjectType(e?.target?.value)}
                        className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <button
                        type="button"
                        onClick={handleCreateNewProjectType}
                        className="px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                      >
                        <Icon name="Check" size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewProjectTypeForm(false);
                          setNewProjectType('');
                        }}
                        className="px-3 py-2 rounded-md border border-input bg-background hover:bg-muted transition-colors"
                      >
                        <Icon name="X" size={18} />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Add a custom project type not in the standard list.
                    </p>
                  </div>
                )}
              </div>

              {/* Project Country Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Project Country</label>
                <Select
                  options={countryOptions}
                  value={formData?.projectCountry}
                  onChange={(value) => handleInputChange('projectCountry', value)}
                  placeholder="Select or search for country (optional)"
                  searchable={true}
                  clearable={true}
                />
              </div>
            </div>

            {/* Client Name and Project Owner - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              {/* Client Name Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Client Name</label>
                {!showNewClientForm ? (
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <Select
                        options={clientOptions}
                        value={formData?.clientId}
                        onChange={(value) => handleInputChange('clientId', value)}
                        placeholder="Select or search for a client (optional)"
                        searchable={true}
                        clearable={true}
                        disabled={loadingClients}
                        loading={loadingClients}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowNewClientForm(true)}
                      className="px-3 py-2 rounded-md border border-input bg-background hover:bg-muted transition-colors flex-shrink-0"
                      title="Add new client"
                    >
                      <Icon name="Plus" size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter new client name"
                        value={newClientName}
                        onChange={(e) => setNewClientName(e?.target?.value)}
                        className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <button
                        type="button"
                        onClick={handleCreateNewClient}
                        className="px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                      >
                        <Icon name="Check" size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewClientForm(false);
                          setNewClientName('');
                        }}
                        className="px-3 py-2 rounded-md border border-input bg-background hover:bg-muted transition-colors"
                      >
                        <Icon name="X" size={18} />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Quick create a new client. You can add more details later in Client Management.
                    </p>
                  </div>
                )}
              </div>

              {/* Project Owner Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Project Owner <span className="text-destructive">*</span>
                </label>
                <Select
                  options={staffOptions}
                  value={formData?.owner}
                  onChange={(value) => handleInputChange('owner', value)}
                  placeholder="Select or search for project owner"
                  searchable={true}
                  clearable={true}
                  disabled={loadingStaff}
                  loading={loadingStaff}
                />
                {errors?.owner && (
                  <p className="text-sm text-destructive">{errors?.owner}</p>
                )}
              </div>
            </div>

            {/* Description - Moved to bottom */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Description <span className="text-destructive">*</span>
              </label>
              <textarea
                placeholder="Describe the project goals and scope"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                className="w-full h-24 px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {errors?.description && (
                <p className="text-sm text-destructive">{errors?.description}</p>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            iconName="Check" 
            onClick={handleSubmit}
            disabled={uploading}
          >
            {uploading ? 'Saving...' : editingProject ? 'Update Project' : 'Create Project'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;